import {
  createPortableArchiveFromFixture,
  readExistingStudySnapshot,
} from '@hoedokloop/storage-indexeddb';
import type { StudyFixture } from '@hoedokloop/domain/fixtures';
import type { Instant, PhotoAsset } from '@hoedokloop/domain';
import { useEffect, useState } from 'react';
import { BrowserRouter, Link, Navigate, Route, Routes } from 'react-router';

import { BrandMark } from './components/brand-mark';

function RecoveryPhoto({ photo }: { photo: PhotoAsset | undefined }) {
  const [url, setUrl] = useState<string>();
  useEffect(() => {
    if (!photo) return;
    const next = URL.createObjectURL(photo.original);
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [photo]);
  return url ? <img className="pn-note-photo" src={url} alt="보관한 오답 원본" /> : null;
}

function RecoveryOnlyPage({ offlineUnverified = false }: { offlineUnverified?: boolean }) {
  const [snapshot, setSnapshot] = useState<StudyFixture>();
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const load = async () => {
    try {
      setSnapshot(await readExistingStudySnapshot());
      setError(undefined);
    } catch {
      setError('기록을 열지 못했어요. 저장소를 지우지 말고 다시 시도해 주세요.');
    }
  };
  useEffect(() => {
    void load();
  }, []);
  const exportZip = async () => {
    if (!snapshot) return;
    setBusy(true);
    setError(undefined);
    try {
      const bytes = await createPortableArchiveFromFixture({
        fixture: snapshot,
        exportedAt: new Date().toISOString() as Instant,
        appVersion: '0.1.0',
      });
      const url = URL.createObjectURL(new Blob([bytes as BlobPart], { type: 'application/zip' }));
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `hoedokloop-${new Date().toISOString().slice(0, 10)}.hoedokloop.zip`;
      anchor.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      setError('백업 파일을 만들지 못했어요. 원본 기록은 그대로 두고 다시 시도해 주세요.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="pn-page">
      <h1>내 기록 열기와 백업</h1>
      {offlineUnverified ? (
        <p role="status">
          오프라인이라 연결 상태를 확인하지 못했어요. 기존 기록 읽기와 ZIP 백업만 가능해요.
        </p>
      ) : null}
      <p>복구 화면에서는 기록을 읽고 ZIP으로 받을 수 있어요. 기록을 바꾸거나 삭제하지 않아요.</p>
      {error ? (
        <p role="alert">
          {error} <button onClick={() => void load()}>다시 불러오기</button>
        </p>
      ) : !snapshot ? (
        <p role="status">기록을 여는 중이에요.</p>
      ) : (
        <>
          <button
            className="pn-button"
            data-tone="primary"
            disabled={busy}
            onClick={() => void exportZip()}
          >
            백업 ZIP 파일 받기
          </button>
          <section>
            <h2>시험 {snapshot.exams.length}개</h2>
            {snapshot.exams.map((exam) => (
              <p key={exam.id}>
                {exam.title} · {exam.examDate ?? '시험일 미정'}
              </p>
            ))}
          </section>
          <section>
            <h2>공부 기록 {snapshot.studyLogs.length}개</h2>
            {snapshot.studyLogs.map((log) => (
              <details key={log.id}>
                <summary>
                  {log.studyDate} · {log.round}회독 {log.unitStart}–{log.unitEnd}
                </summary>
                <p>{log.memo}</p>
              </details>
            ))}
          </section>
          <section>
            <h2>오답 {snapshot.wrongNotes.length}개</h2>
            {snapshot.wrongNotes.map((note) => (
              <details key={note.id} open>
                <summary>{note.text || '사진 오답'}</summary>
                <p>{note.memo}</p>
                {note.photoAssetId ? (
                  <RecoveryPhoto
                    photo={snapshot.photoAssets.find((photo) => photo.id === note.photoAssetId)}
                  />
                ) : null}
              </details>
            ))}
          </section>
          <section>
            <h2>점수 {snapshot.mockScores?.length ?? 0}회</h2>
            <h2>사진함 {snapshot.noteInbox?.length ?? 0}장</h2>
          </section>
        </>
      )}
    </div>
  );
}

/** This branch mounts no StudyProvider, settings hook, reminder, or writable route. */
export function RecoveryOnlyApp({ offlineUnverified = false }: { offlineUnverified?: boolean }) {
  return (
    <BrowserRouter>
      <div className="pn-app-shell">
        <a className="pn-skip-link" href="#main-content">
          본문으로 바로가기
        </a>
        <header className="pn-topbar">
          <Link aria-label="회독루프 복구" className="pn-topbar__brand" to="/recovery">
            <BrandMark />
          </Link>
        </header>
        <main className="pn-main" id="main-content">
          <Routes>
            <Route
              path="/recovery"
              element={<RecoveryOnlyPage offlineUnverified={offlineUnverified} />}
            />
            <Route
              path="/settings/data"
              element={<RecoveryOnlyPage offlineUnverified={offlineUnverified} />}
            />
            <Route path="*" element={<Navigate to="/recovery" replace />} />
          </Routes>
        </main>
      </div>
    </BrowserRouter>
  );
}
